<script>
import { mapGetters } from 'vuex';
import BarChart from 'shared/components/charts/BarChart.vue';
import { GROUP_BY_FILTER } from './constants';
import { formatDateChart, formatWeekRange } from 'shared/helpers/dateFormatter';

// Overview panel for sent messages. The "Total" hero shows the number plus a
// delivered/not-delivered composition bar (both clickable); the other tiles are
// the origin buckets (AI / API / Attendant). Every tile/legend is a selector
// that swaps the chart. "attendant" has no direct metric — it is derived as
// total - ia - api - automated. All series are already in the store.
const DIRECT_METRIC = {
  total: 'outgoing_messages_count',
  delivered: 'delivered_messages_count',
  undelivered: 'undelivered_messages_count',
  ia: 'ia_messages_count',
  api: 'api_messages_count',
};
const COLORS = {
  total: 'rgb(31, 147, 255)',
  delivered: 'rgb(34, 197, 94)',
  undelivered: 'rgb(239, 68, 68)',
  ia: 'rgb(139, 92, 246)',
  api: 'rgb(20, 184, 166)',
  attendant: 'rgb(245, 158, 11)',
};

export default {
  name: 'SentMessagesPanel',
  components: { BarChart },
  props: {
    groupBy: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return { selected: 'total' };
  },
  computed: {
    ...mapGetters({ accountReport: 'getAccountReports' }),
    colors() {
      return COLORS;
    },
    tileByKey() {
      const keys = [
        'total',
        'delivered',
        'undelivered',
        'ia',
        'api',
        'attendant',
      ];
      return Object.fromEntries(
        keys.map(key => {
          const series = this.seriesFor(key);
          const value = series.reduce(
            (acc, el) => acc + (Number(el.value) || 0),
            0
          );
          return [
            key,
            {
              key,
              color: COLORS[key],
              label: this.$t(`REPORT.SENT_PANEL.${key.toUpperCase()}`),
              value,
            },
          ];
        })
      );
    },
    deliveredWidth() {
      const total = this.tileByKey.total.value;
      return total ? (this.tileByKey.delivered.value / total) * 100 : 0;
    },
    undeliveredWidth() {
      const total = this.tileByKey.total.value;
      return total ? (this.tileByKey.undelivered.value / total) * 100 : 0;
    },
    deliveryRate() {
      return Math.round(this.deliveredWidth);
    },
    activeSeries() {
      return this.seriesFor(this.selected);
    },
    isFetching() {
      return this.accountReport.isFetching.outgoing_messages_count;
    },
    collection() {
      return {
        labels: this.activeSeries.map(el => this.formatLabel(el.timestamp)),
        datasets: [
          {
            type: 'bar',
            label: this.tileByKey[this.selected].label,
            backgroundColor: COLORS[this.selected],
            data: this.activeSeries.map(el => el.value),
          },
        ],
      };
    },
    chartOptions() {
      return {
        scales: {
          x: { grid: { drawOnChartArea: false } },
          y: {
            type: 'linear',
            position: 'left',
            beginAtZero: true,
            grid: { drawOnChartArea: false },
          },
        },
      };
    },
  },
  methods: {
    seriesFor(key) {
      const { data } = this.accountReport;
      if (key !== 'attendant') {
        return data[DIRECT_METRIC[key]] || [];
      }
      const outgoing = data.outgoing_messages_count || [];
      const ia = data.ia_messages_count || [];
      const api = data.api_messages_count || [];
      const automated = data.automated_messages_count || [];
      const at = (series, i) => Number(series[i]?.value) || 0;
      return outgoing.map((el, i) => ({
        timestamp: el.timestamp,
        value: Math.max(
          at(outgoing, i) - at(ia, i) - at(api, i) - at(automated, i),
          0
        ),
      }));
    },
    tileClass(key) {
      return this.selected === key
        ? 'border-n-brand bg-n-alpha-2'
        : 'border-n-weak bg-n-solid-1 hover:border-n-slate-6';
    },
    sharePct(key) {
      const total = this.tileByKey.total.value;
      if (!total) {
        return 0;
      }
      return Math.round((this.tileByKey[key].value / total) * 100);
    },
    formatLabel(timestamp) {
      const period = this.groupBy?.period;
      if (period === GROUP_BY_FILTER[2].period) {
        return formatWeekRange(timestamp);
      }
      if (period === GROUP_BY_FILTER[3].period) {
        return formatDateChart(timestamp, 'month');
      }
      if (period === GROUP_BY_FILTER[4].period) {
        return formatDateChart(timestamp, 'year');
      }
      return formatDateChart(timestamp, 'day');
    },
  },
};
</script>

<template>
  <div
    class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
  >
    <span class="text-base font-medium text-n-slate-12">
      {{ $t('REPORT.SENT_PANEL.TITLE') }}
    </span>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-4">
      <!-- Total hero: number + delivery composition bar -->
      <div
        class="flex flex-col p-4 border rounded-lg border-n-weak bg-n-solid-1"
      >
        <button
          class="flex flex-col items-start gap-1 p-1 -m-1 text-left rounded-md transition-colors"
          :class="selected === 'total' ? 'bg-n-alpha-2' : 'hover:bg-n-alpha-1'"
          @click="selected = 'total'"
        >
          <span class="flex items-center gap-1.5 text-sm text-n-slate-11">
            <span
              class="inline-block rounded-sm size-2.5"
              :style="{ backgroundColor: colors.total }"
            />
            {{ tileByKey.total.label }}
          </span>
          <span class="text-3xl font-semibold text-n-slate-12">
            {{ tileByKey.total.value.toLocaleString() }}
          </span>
        </button>

        <div
          class="flex w-full h-2 mt-4 overflow-hidden rounded-full bg-n-slate-3"
        >
          <div
            class="h-full"
            :style="{
              width: `${deliveredWidth}%`,
              backgroundColor: colors.delivered,
            }"
          />
          <div
            class="h-full"
            :style="{
              width: `${undeliveredWidth}%`,
              backgroundColor: colors.undelivered,
            }"
          />
        </div>
        <div class="mt-1.5 text-xs text-n-slate-10">
          {{ deliveryRate }}% {{ tileByKey.delivered.label.toLowerCase() }}
        </div>

        <div class="grid grid-cols-2 gap-2 mt-3">
          <button
            v-for="key in ['delivered', 'undelivered']"
            :key="key"
            class="flex flex-col gap-0.5 p-2 text-left border rounded-lg transition-colors"
            :class="tileClass(key)"
            @click="selected = key"
          >
            <span class="flex items-center gap-1 text-xs text-n-slate-11">
              <span
                class="inline-block rounded-sm size-2"
                :style="{ backgroundColor: colors[key] }"
              />
              {{ tileByKey[key].label }}
            </span>
            <span class="text-lg font-semibold text-n-slate-12">
              {{ tileByKey[key].value.toLocaleString() }}
            </span>
          </button>
        </div>
      </div>

      <!-- Origin buckets -->
      <div class="grid grid-cols-3 gap-2">
        <button
          v-for="key in ['ia', 'api', 'attendant']"
          :key="key"
          class="flex flex-col h-full gap-2 p-3 text-left border rounded-lg transition-colors"
          :class="tileClass(key)"
          @click="selected = key"
        >
          <span class="flex items-center gap-1.5 text-sm text-n-slate-11">
            <span
              class="inline-block rounded-sm size-2.5"
              :style="{ backgroundColor: colors[key] }"
            />
            {{ tileByKey[key].label }}
          </span>
          <span
            class="flex flex-col items-center justify-center flex-1 text-center gap-0.5"
          >
            <span class="text-2xl font-semibold text-n-slate-12">
              {{ tileByKey[key].value.toLocaleString() }}
            </span>
            <span class="text-xs font-medium text-n-slate-10">
              {{ sharePct(key) }}% {{ $t('REPORT.SENT_PANEL.OF_TOTAL') }}
            </span>
          </span>
        </button>
      </div>
    </div>

    <div class="mt-5 h-72">
      <woot-loading-state
        v-if="isFetching"
        class="text-xs"
        :message="$t('REPORT.LOADING_CHART')"
      />
      <div v-else class="flex items-center justify-center h-72">
        <BarChart
          v-if="activeSeries.length"
          :collection="collection"
          :chart-options="chartOptions"
        />
        <span v-else class="text-sm text-n-slate-10">
          {{ $t('REPORT.SENT_PANEL.NO_DATA') }}
        </span>
      </div>
    </div>
  </div>
</template>
